import React, { useState, useEffect } from 'react';
import {
  X, CalendarCheck, MapPin, ShieldCheck, ShieldOff, Loader2, Camera, Smile, Meh, Frown,
} from 'lucide-react';
import { getCheckInPhotoUrl, verifyPostAdoptionUpdate } from '@rehome/shared';
import type { AnimalFollowupUpdate } from '@rehome/shared';

interface CheckInDetailDialogProps {
  update: AnimalFollowupUpdate | null;
  animalName: string;
  adopterName?: string | null;
  onClose: () => void;
  /** Sikeres visszaigazolás után — a lista frissítéséhez. */
  onVerified?: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  '3_Day': '3 napos helyzetjelentés',
  '3_Week': '3 hetes helyzetjelentés',
  '3_Month': '3 hónapos helyzetjelentés',
  Spontaneous: 'Spontán helyzetjelentés',
};

const MOOD_META: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
  Happy: { label: 'Boldog', icon: <Smile className="w-4 h-4" />, className: 'bg-green-100 text-green-800 border-green-200' },
  Neutral: { label: 'Semleges', icon: <Meh className="w-4 h-4" />, className: 'bg-gray-100 text-gray-700 border-gray-200' },
  Struggling: { label: 'Nehézségek', icon: <Frown className="w-4 h-4" />, className: 'bg-orange-100 text-orange-800 border-orange-200' },
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Egy beküldött helyzetjelentés MINDEN adata, nagy dialogban.
 *
 * A listás nézet szándékosan csak összesítést mutat (l. 043-as migráció): a
 * jelentés szövege akár 1000 karakter, a fotó pedig a privát tárolóból, aláírt
 * linken jön. Ezeket csak akkor kérjük le, amikor a menhely ténylegesen meg
 * akar nézni egy jelentést — vagyis itt.
 */
export const CheckInDetailDialog: React.FC<CheckInDetailDialogProps> = ({
  update, animalName, adopterName, onClose, onVerified,
}) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    setVerified(Boolean(update?.verifiedByShelter));
  }, [update?.id, update?.verifiedByShelter]);

  // A check-in fotó a PRIVÁT tárolóban van (személyes felvétel, pontos GPS
  // mellett) — rövid életű aláírt link kell hozzá, és csak akkor, ha a
  // dialog tényleg megnyílt.
  useEffect(() => {
    let cancelled = false;
    if (!update?.imageUrl) { setPhotoUrl(null); return; }
    setPhotoLoading(true);
    getCheckInPhotoUrl(update.imageUrl).then(url => {
      if (!cancelled) { setPhotoUrl(url); setPhotoLoading(false); }
    });
    return () => { cancelled = true; };
  }, [update?.id, update?.imageUrl]);

  // Escape-re zárás — a nagy dialog máskülönben csapdába ejtheti a
  // billentyűzetet használó felhasználót.
  useEffect(() => {
    if (!update) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [update, onClose]);

  if (!update) return null;

  const handleVerify = async () => {
    setVerifying(true);
    const ok = await verifyPostAdoptionUpdate(update.id, !verified);
    setVerifying(false);
    if (ok) {
      setVerified(!verified);
      onVerified?.();
    }
  };

  const mood = update.mood ? MOOD_META[update.mood] : null;
  const hasGps = update.gpsLat != null && update.gpsLng != null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${animalName} — helyzetjelentés részletei`}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{animalName}</h2>
            <p className="text-sm text-gray-500">
              {TYPE_LABELS[update.updateType] || update.updateType}
              {adopterName ? ` · ${adopterName}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Bezárás"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
              {photoLoading ? (
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              ) : photoUrl ? (
                <img src={photoUrl} alt={`${animalName} helyzetjelentés fotó`} className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <Camera className="w-8 h-8" />
                  <span className="text-xs">Nincs megjeleníthető fotó</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 px-2.5 py-1 rounded-full">
                <CalendarCheck className="w-3.5 h-3.5" /> {formatDateTime(update.createdAt)}
              </span>
              {mood && (
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium border px-2.5 py-1 rounded-full ${mood.className}`}>
                  {mood.icon} {mood.label}
                </span>
              )}
              {hasGps && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-green-100 text-green-800 border border-green-200 px-2.5 py-1 rounded-full">
                  <MapPin className="w-3.5 h-3.5" /> {update.gpsLat!.toFixed(4)}, {update.gpsLng!.toFixed(4)}
                </span>
              )}
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Az örökbefogadó beszámolója
              </h3>
              {update.notes ? (
                // `whitespace-pre-wrap`: a beküldő bekezdései maradjanak meg —
                // egy 1000 karakteres beszámoló egy tömbben olvashatatlan.
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{update.notes}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">Nem írt szöveges beszámolót.</p>
              )}
            </div>

            {update.shelterNotes && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Menhelyi megjegyzés</h3>
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{update.shelterNotes}</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-500">
            {verified
              ? 'A menhely visszaigazolta ezt a jelentést.'
              : 'Még nincs visszaigazolva.'}
          </p>
          <button
            type="button"
            onClick={handleVerify}
            disabled={verifying}
            className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border transition-colors disabled:opacity-60 ${
              verified
                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                : 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
            }`}
          >
            {verifying
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : verified ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
            {verified ? 'Visszaigazolva — visszavonás' : 'Visszaigazolom'}
          </button>
        </div>
      </div>
    </div>
  );
};
